import {
  useEffect, useRef, useState,
} from 'react';
import { useRelayEnvironment } from 'react-relay';

import FlashesStore from 'src/stores/FlashesStore';
import logger from 'src/util/logger';
import { TradeListLoadingActions } from 'src/components/Trade/TradeList/reducer/constants';

import { fetchPropertyTrades } from '../helpers/dataBatching';

/**
 * Sorts an array of trades chronologically by timestamp, then by meter identifier, then by
 * trade UUID for trades with identical timestamps and identifiers.
 *
 * This function provides a consistent sorting order for trade data:
 * - Primary sort: Trades are ordered chronologically by their interval timestamp (earliest first)
 * - Secondary sort: For trades occurring at the same timestamp, they are sorted alphabetically
 *   by meter identifier
 * - Tertiary sort: For trades with the same timestamp and meter identifier, they are sorted
 *   alphabetically by trade UUID
 * - Edge case handling: Trades without timestamps default to 0, trades without meter identifiers or
 *   UUIDs default to empty string
 * @param {Array<object>} trades - Array of trade objects to sort
 * @returns {Array<object>} New array of trades sorted by timestamp (chronologically), then by meter
 * identifier, then by trade UUID
 * @example
 * const trades = [
 *   { id: 'trade-2', interval: { timestamp: 1500 },
 * meter: { identifier: 'M-2', title: 'Meter B' } },
 *   { id: 'trade-1', interval: { timestamp: 1000 },
 * meter: { identifier: 'M-1', title: 'Meter A' } },
 *   { id: 'trade-3', interval: { timestamp: 1500 },
 * meter: { identifier: 'M-1', title: 'Meter A' } },
 * ];
 *
 * const sorted = sortTradesByTimestampAndMeter(trades);
 * // Result: [trade-1 (1000, M-1), trade-3 (1500, M-1), trade-2 (1500, M-2)]
 */
export const sortTradesByTimestampAndMeter = (trades) => {
  if (!Array.isArray(trades)) {
    return [];
  }

  return [...trades].sort((a, b) => {
    // Primary sort: by timestamp (chronologically)
    const aTimestamp = a.interval?.timestamp || 0;
    const bTimestamp = b.interval?.timestamp || 0;
    if (aTimestamp !== bTimestamp) {
      return aTimestamp - bTimestamp;
    }

    // Secondary sort: by meter identifier for trades with the same timestamp
    const aIdentifier = a.meter?.identifier ?? '';
    const bIdentifier = b.meter?.identifier ?? '';
    const identifierCompare = String(aIdentifier).localeCompare(String(bIdentifier));
    if (identifierCompare !== 0) {
      return identifierCompare;
    }

    // Tertiary sort: by trade UUID for trades with the same timestamp and meter identifier
    const aUuid = a.id ?? '';
    const bUuid = b.id ?? '';
    return String(aUuid).localeCompare(String(bUuid));
  });
};

/**
 * Transforms meter data into a flat array of trades with linked rules and meter information.
 * Sorts trades chronologically by timestamp, then by meter identifier, then by trade UUID.
 * @param {object} meters - The meters connection object from GraphQL
 * @param {object} meters.edges - Array of meter edges from GraphQL connection
 * @returns {Array} Array of trade objects with linked rule and meter data, sorted by timestamp,
 * meter identifier, then trade UUID
 */
export const getTradesFromMeters = (meters) => {
  const allTrades = [];

  if (meters?.edges?.length === 0) {
    return allTrades;
  }
  meters?.edges?.forEach((el) => {
    const { node: meter } = el || {};
    const {
      rules: ruleConn, trades: tradeConn, id: meterId,
      identifier: meterIdentifier, title: meterTitle,
    } = meter || {};

    if (!tradeConn) {
      return;
    }

    const rulesByUuid = new Map();

    ruleConn?.edges?.forEach((edge) => {
      const rule = edge?.node;
      if (rule?.id) {
        rulesByUuid.set(rule.id, rule);
      }
    });

    tradeConn.edges?.forEach((edge) => {
      const { node: trade } = edge || {};
      // Link trade to its corresponding rule via ruleId lookup
      const linkedRule = trade?.ruleId ? rulesByUuid.get(trade.ruleId) : null;

      allTrades.push({
        ...trade,
        meter: { id: meterId, identifier: meterIdentifier, title: meterTitle },
        rule: linkedRule,
      });
    });
  });

  // Sort trades by timestamp (chronologically), then by meter identifier, then by trade UUID
  return sortTradesByTimestampAndMeter(allTrades);
};

/**
 * Creates a filter function that checks if a trade matches the specified direction
 * and type filters.
 * @param {Array<string>} direction - The list of trade direction filters.
 * @param {Array<string>} type - The list of trade type filters.
 * @returns {Function} - A function that takes a trade object and returns a boolean
 * indicating if the trade matches the filters.
 */
export const hasTradeDirectionAndType = (direction, type) => {
  const filterFunction = (trade) => {
    if (direction.length === 0 && type.length === 0) { return true; }
    const hasDirection = direction.length === 0 || direction.includes(trade.direction);
    const hasType = type.length === 0 || type.includes(trade.type);
    return hasDirection && hasType;
  };
  return filterFunction;
};

/**
 * Custom hook that orchestrates three-stage trade data fetching for a property.
 *
 * Fetches trade data in three sequential stages:
 * 1. Property meters - Retrieves all meters for the property
 * 2. Trade rules - Fetches rules for each meter within the time range
 * 3. Trades - Retrieves all trades for each meter within the time range
 * @param {object} params - Hook parameters
 * @param {object} params.property - Property object from GraphQL Property fragment
 * @param {object} params.timeRange - Time range object with start and finish
 * @param {Function} params.stateDispatchFunc - Optional actions object from
 * @returns {{combinedData: object}}
 * Trade data and state object
 */
const useTradeData = ({
  property,
  stateDispatchFunc,
  timeRange,
}) => {
  const environment = useRelayEnvironment();
  const { start, finish } = timeRange || {};
  const { id: propertyId } = property || {};

  const [combinedData, setCombinedData] = useState(null);

  // Track if we're currently fetching data to prevent parallel fetches.
  const isFetchingData = useRef(false);

  useEffect(() => {
    if (!property || !property.id || !timeRange || !timeRange.start || !timeRange.finish) {
      logger.error('No property ID or requested period found for trade data fetching');

      return;
    }

    fetchPropertyTrades({
      propertyId,
      start,
      finish,
      environment,
      onDataReceived: (mergedData) => {
        logger.log('Received merged three-stage trade data with property:', !!mergedData?.property);
        setCombinedData(mergedData);
      },
      onError: (fetchError, message) => {
        logger.error('Error in three-stage trade data fetching:', fetchError);
        FlashesStore.flash(FlashesStore.ERROR, message || 'Error fetching trade data');
        stateDispatchFunc({ type: TradeListLoadingActions.RAISE_ERROR, error: fetchError });
      },
      isFetchingRef: isFetchingData,
      pageSize: 1000,
      stateDispatchFunc,
    });
  }, [
    property,
    timeRange,
    environment,
  ]);

  return {
    combinedData,
  };
};

export default useTradeData;
